/**
 * AdminQrBadges — admin page for issuing/revoking QR login badges.
 *
 * Pick a tenant, pick a user type (student/teacher/parent), search by name,
 * and click "Issue QR" on a row to open the badge dialog. Revocation happens
 * from inside the same dialog.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, QrCode, Search, Loader2 } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';
import IssueQrBadgeDialog from '@/components/school/IssueQrBadgeDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path) => {
  const r = await fetch(`${API_URL}/api${path}`, { headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

export default function AdminQrBadges() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState('');
  const [userType, setUserType] = useState('student');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const { data: tenants = [], isLoading: loadingTenants } = useQuery({
    queryKey: ['school-tenants'],
    queryFn: () => api('/school/tenants').catch(() => []),
  });

  const userPath = userType === 'student' ? 'students' : userType === 'teacher' ? 'teachers' : 'guardians';

  const { data: usersData, isLoading: loadingUsers } = useQuery({
    queryKey: ['qr-badges-users', tenantId, userType],
    queryFn: () => api(`/school/tenants/${tenantId}/${userPath}`),
    enabled: !!tenantId,
  });
  
  // API returns {students: [...]} or {teachers: [...]} or {guardians: [...]}
  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData)) return usersData;
    // Extract array from response object
    return usersData[userPath] || usersData.students || usersData.teachers || usersData.guardians || [];
  }, [usersData, userPath]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter((u) => {
      const name = `${u.first_name || u.name || ''} ${u.last_name || ''}`.toLowerCase();
      const email = (u.email || '').toLowerCase();
      const adm = (u.admission_number || '').toLowerCase();
      return name.includes(q) || email.includes(q) || adm.includes(q);
    });
  }, [users, search]);

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="qr-badges-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
              <QrCode className="h-7 w-7 text-indigo-600" /> QR Login Badges
            </h1>
            <p className="text-sm text-slate-500">Issue, print and revoke passwordless login badges for school users.</p>
          </div>
        </div>

        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Pick who to issue for</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            <div>
              <Label>School</Label>
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger data-testid="qr-badges-tenant-select"><SelectValue placeholder={loadingTenants ? 'Loading…' : 'Select school'} /></SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => <SelectItem key={t.id || t._id} value={t.id || t._id}>{t.name || t.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>User type</Label>
              <Select value={userType} onValueChange={setUserType}>
                <SelectTrigger data-testid="qr-badges-user-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Students</SelectItem>
                  <SelectItem value="teacher">Teachers</SelectItem>
                  <SelectItem value="parent">Parents / Guardians</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Search</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-8"
                  placeholder="Name, email, admission #"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="qr-badges-search"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {!tenantId && (
          <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-500">Select a school to begin.</CardContent></Card>
        )}

        {tenantId && (
          <Card>
            <CardContent className="p-0">
              {loadingUsers ? (
                <div className="py-16 text-center text-slate-500"><Loader2 className="h-5 w-5 inline mr-2 animate-spin" /> Loading {userType}s…</div>
              ) : filtered.length === 0 ? (
                <div className="py-16 text-center text-slate-500">No {userType}s found.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Identifier</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.slice(0, 200).map((u) => {
                      const name = userType === 'parent'
                        ? (u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim())
                        : `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.name || '—';
                      const ident = u.admission_number || u.employee_code || u.phone || '—';
                      return (
                        <TableRow key={u.id || u._id} data-testid={`qr-badges-row-${u.id || u._id}`}>
                          <TableCell className="font-medium">{name || '—'}</TableCell>
                          <TableCell className="text-xs text-slate-500">{ident}</TableCell>
                          <TableCell className="text-xs text-slate-500">{u.email || '—'}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelected({ id: u.id || u._id, name })}
                              data-testid={`issue-qr-btn-${u.id || u._id}`}
                            >
                              <QrCode className="h-4 w-4 mr-1" /> Issue QR
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {selected && (
          <IssueQrBadgeDialog
            open={!!selected}
            onClose={() => setSelected(null)}
            userType={userType}
            linkedId={selected.id}
            tenantId={tenantId}
            name={selected.name}
          />
        )}
      </div>
    </div>
  );
}
